import type {
  IAlarm,
  IPassportConfiguration,
  IPassportReference,
} from '@obvious.tech/constellation'
import { z } from 'zod'

import type { TEntity } from 'src/utils/api'
import { isGroupName } from './useAlarmAssignedEntity'

export type TPassportConfiguration = TEntity<IPassportConfiguration>

export const passportScope = (passportId: string): string =>
  `/passport/${passportId}/`

const logoIdSchema = z.object({
  logo: z.string().trim().min(1),
})

const toPassportId = (
  reference: IPassportReference | undefined,
): string | null => {
  const passportId = reference?.passportId
  if (typeof passportId !== 'string' || passportId.length === 0) return null

  // an assignment may carry a group instead of a passport, see issue #5421
  const userName = reference?.userName
  if (typeof userName === 'string' && isGroupName(userName)) return null

  return passportId
}

export const getAssignedPassportIds = (alarm: TEntity<IAlarm>): string[] => {
  const assigned = (alarm.assignedPassports ?? []).map(toPassportId)
  const deprecated = toPassportId({ passportId: alarm.assignedPassportId })

  const passportIds = [...assigned, deprecated].filter(
    (it): it is string => it !== null,
  )

  return Array.from(new Set(passportIds))
}

const covers = (group: string, passportGroups: string[]): boolean => {
  if (group.length === 0) return false

  const prefix = group.toLowerCase()
  return passportGroups.some(it => it.toLowerCase().startsWith(prefix))
}

const getLogoId = (configuration: unknown): string | null => {
  const parsed = logoIdSchema.safeParse(configuration)
  return parsed.success ? parsed.data.logo : null
}

export const selectLogoImageId = ({
  passportId,
  passportGroups,
  passportConfigurations,
}: {
  passportId: string
  passportGroups: string[]
  passportConfigurations: TPassportConfiguration[]
}): string | null => {
  const scope = passportScope(passportId)
  let clientLogo: { group: string; logo: string } | null = null

  for (const { groups, configuration } of passportConfigurations) {
    const logo = getLogoId(configuration)
    if (logo === null) continue

    const scopes = groups ?? []
    if (scopes.includes(scope)) return logo

    for (const group of scopes) {
      if (!covers(group, passportGroups)) continue
      if (clientLogo === null || group.length > clientLogo.group.length)
        clientLogo = {
          group,
          logo,
        }
    }
  }

  return clientLogo?.logo ?? null
}

const FILE_SIGNATURES: Array<{ mimeType: string; bytes: number[] }> = [
  {
    mimeType: 'image/png',
    bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
  },
  {
    mimeType: 'image/jpeg',
    bytes: [0xff, 0xd8, 0xff],
  },
]

const SIGNATURE_BYTES = Math.max(...FILE_SIGNATURES.map(it => it.bytes.length))

const BASE64_MARKER = 'base64,'

const decodeLeadingBytes = (base64: string): number[] => {
  const quadruplets = Math.ceil(SIGNATURE_BYTES / 3)

  try {
    const binary = atob(base64.slice(0, quadruplets * 4))
    return Array.from(binary, character => character.charCodeAt(0))
  } catch {
    return []
  }
}

const getMimeType = (base64: string): string | null => {
  const bytes = decodeLeadingBytes(base64)

  const signature = FILE_SIGNATURES.find(it =>
    it.bytes.every((byte, index) => bytes[index] === byte),
  )

  return signature?.mimeType ?? null
}

export const toImageSource = (imageData: string): string | null => {
  const markerIndex = imageData.indexOf(BASE64_MARKER)
  const base64 = (
    markerIndex === -1
      ? imageData
      : imageData.slice(markerIndex + BASE64_MARKER.length)
  ).trim()

  const mimeType = getMimeType(base64)
  if (mimeType === null) return null

  return `data:${mimeType};${BASE64_MARKER}${base64}`
}
